import { useJobillaOrgId } from '@/hooks'
import { useCustomFontIndex } from '@jobilla/nexus-sdk'
import { useAtom, useAtomValue } from 'jotai'
import { useCallback, useMemo } from 'react'
import { Button, LibraryDialog } from '../../../ui'
import { fontPickerOpenAtom, fontPickerPromiseAtom, fontPickerSelectedFontIdAtom } from '../atoms'
import { useFontPickerStore } from '../store'
import type { FontSelection } from '../types'
import { FontGrid } from './font-grid'

export function FontPickerPicker() {
	const store = useFontPickerStore()
	const [isOpen, setIsOpen] = useAtom(fontPickerOpenAtom, { store })
	const [promise, setPromise] = useAtom(fontPickerPromiseAtom, { store })

	const handleClose = useCallback(() => {
		promise?.resolve(undefined)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	const handleSelect = useCallback((font: FontSelection) => {
		promise?.resolve(font)
		setPromise(null)
		setIsOpen(false)
	}, [promise, setPromise, setIsOpen])

	return (
		<LibraryDialog open={isOpen} onClose={handleClose}>
			{isOpen
				? (
					<FontPickerPickerContent
						onSelect={handleSelect}
						onClose={handleClose}
					/>
				)
				: null}
		</LibraryDialog>
	)
}

function FontPickerPickerContent({
	onSelect,
	onClose,
}: {
	onSelect: (font: FontSelection) => void
	onClose: () => void
}) {
	const store = useFontPickerStore()
	const organizationId = useJobillaOrgId()
	const { data: fonts } = useCustomFontIndex(organizationId)
	const selectedFontId = useAtomValue(fontPickerSelectedFontIdAtom, { store })

	const selectedFont = useMemo(
		() => fonts?.find((f) => f.id === selectedFontId),
		[fonts, selectedFontId],
	)

	const handleSelectFont = useCallback(() => {
		if (selectedFont) {
			onSelect({
				url: selectedFont.url,
				name: selectedFont.displayName,
				weight: selectedFont.weight,
				style: selectedFont.style,
			})
		}
	}, [selectedFont, onSelect])

	return (
		<FontGrid
			mode="picker"
			showCloseButton={true}
			onClose={onClose}
			committalBar={
				<div className="flex justify-end p-4">
					<Button size="normal" type="committal" onClick={handleSelectFont} disabled={!selectedFont}>
						Select This Font
					</Button>
				</div>
			}
		/>
	)
}
